/**
 * Demo group seed (ARCHITECTURE §3.2). Two UAE entities — a Free Zone company
 * and a Mainland company — with realistic AED transactions so tie-out,
 * statements, VAT, FX, and intercompany elimination are all visible on first
 * run. Every entry is balanced, so the trial balance ties by construction.
 *
 * The corporate-tax accrual is computed FROM the seeded profit using the core
 * engine, so the seed is self-consistent with the tax settings.
 */
import type {
  Account,
  Entity,
  FxRate,
  JournalEntry,
  JournalLine,
  Money,
  TaxSettings,
} from "@core/index";
import { DEFAULT_CHART, estimateCt, incomeStatement, money } from "@core/index";
import type { Currency } from "@core/index";
import type { AppMeta } from "./repository";
import type { Role, Session, User } from "./identity";

const BASE: Currency = "AED";

/** Stable tenant id for the demo group (matches DEMO_META.orgId). */
const DEMO_ORG_ID = "org-meridian";

/** AED helper: whole dirhams → Money in fils. */
function aed(dirhams: number): Money {
  return money(Math.round(dirhams * 100), BASE);
}
/** AED helper for amounts already carrying fils precision. */
function aedFils(fils: number): Money {
  return money(fils, BASE);
}

const zeroAed = money(0, BASE);
function dr(accountCode: string, amount: Money, extra: Partial<JournalLine> = {}): JournalLine {
  return { accountCode, debit: amount, credit: zeroAed, ...extra };
}
function cr(accountCode: string, amount: Money, extra: Partial<JournalLine> = {}): JournalLine {
  return { accountCode, credit: amount, debit: zeroAed, ...extra };
}

export const SEED_ENTITIES: readonly Entity[] = [
  { id: "fz", name: "Meridian Trading FZ-LLC", kind: "freezone", baseCurrency: BASE },
  { id: "ml", name: "Meridian Distribution LLC", kind: "mainland", baseCurrency: BASE },
];

export const SEED_ACCOUNTS: readonly Account[] = DEFAULT_CHART;

const SEED_FX_RATES: readonly FxRate[] = [
  { currency: "USD", rate: 3.673, asOf: "2026-07-20" },
  { currency: "EUR", rate: 3.991, asOf: "2026-07-20" },
  { currency: "GBP", rate: 4.682, asOf: "2026-07-20" },
];

/** Metadata stamped alongside the demo seed so the wizard is skipped in dev. */
export const DEMO_META: AppMeta = {
  setupComplete: true,
  orgId: DEMO_ORG_ID,
  orgName: "Meridian Group",
  fiscalYearStartMonth: 1,
};

/**
 * Demo identity (dev + tests only — never seeded in production; SEED_DEMO_DATA
 * is gated on import.meta.env.DEV). The demo owner uses a plain `demo$` verifier
 * so a developer can sign in with password `demo1234`; real users always get a
 * PBKDF2 verifier via hashPassword(). Fixed timestamps keep the seed clock-free.
 */
export const DEMO_PASSWORD = "demo1234";

/** Build a demo user in the given role, sharing the demo owner's credentials. */
export function buildDemoUser(role: Role = "owner"): User {
  return {
    id: "usr-demo",
    orgId: DEMO_ORG_ID,
    name: role === "owner" ? "Aisha Rahman" : `Demo ${role[0]!.toUpperCase()}${role.slice(1)}`,
    email: "owner@meridian.example",
    role,
    passwordHash: `demo$${DEMO_PASSWORD}`,
    createdAt: 1735689600000, // 2025-01-01, fixed
  };
}

export const DEMO_USERS: readonly User[] = [buildDemoUser("owner")];

/** A long-lived demo session so dev/tests boot straight past the sign-in gate. */
export const DEMO_SESSION: Session = {
  userId: "usr-demo",
  orgId: DEMO_ORG_ID,
  token: "demo-token",
  createdAt: 1735689600000,
  expiresAt: 4102444800000, // 2100-01-01, effectively non-expiring
};

/**
 * Fresh-organization tax/currency defaults for a real go-live (no demo
 * balances). The Setup Wizard writes these once the base currency is chosen;
 * FX rates seed every non-base currency so Settings has rows to edit.
 */
export function buildInitialSettings(baseCurrency: Currency): TaxSettings {
  return {
    baseCurrency,
    vatRate: 0.05,
    ctRate: 0.09,
    ctThreshold: 375_000_00, // AED 375,000 in fils
    sbReliefRevenueCeiling: null,
    fxRates: SEED_FX_RATES.filter((r) => r.currency !== baseCurrency),
  };
}

export const SEED_SETTINGS: TaxSettings = {
  baseCurrency: BASE,
  vatRate: 0.05,
  ctRate: 0.09,
  ctThreshold: 375_000_00, // AED 375,000 in fils
  sbReliefRevenueCeiling: null, // disabled for this group (revenue > AED 3m)
  fxRates: SEED_FX_RATES,
};

/** Base (pre-CT) journal entries. CT accrual is appended by buildSeedEntries(). */
const BASE_ENTRIES: readonly JournalEntry[] = [
  // ---- Free Zone entity ----
  {
    id: "je-1001", ref: "JE-1001", date: "2026-01-05",
    description: "Opening share capital", entityId: "fz",
    txnCurrency: BASE, status: "posted",
    lines: [dr("1100", aed(500_000)), cr("3000", aed(500_000))],
  },
  {
    id: "je-1005", ref: "JE-1005", date: "2026-01-12",
    description: "Fund USD operating account", entityId: "fz",
    txnCurrency: BASE, status: "posted",
    lines: [dr("1105", aed(300_000)), cr("1100", aed(300_000))],
  },
  {
    id: "je-1012", ref: "JE-1012", date: "2026-02-08",
    description: "Sales — Q1 exports (standard-rated)", entityId: "fz",
    txnCurrency: BASE, status: "posted",
    lines: [
      dr("1200", aed(1_260_000)),
      cr("4000", aed(1_200_000), { taxCode: "SR", ctCategory: "deductible" }),
      cr("2100", aed(60_000), { taxCode: "SR" }),
    ],
  },
  {
    id: "je-1018", ref: "JE-1018", date: "2026-03-03",
    description: "Cash collected from customers", entityId: "fz",
    txnCurrency: BASE, status: "posted",
    lines: [dr("1100", aed(420_000)), cr("1200", aed(420_000))],
  },
  {
    id: "je-1022", ref: "JE-1022", date: "2026-03-20",
    description: "Cost of sales — inventory purchased", entityId: "fz",
    txnCurrency: BASE, status: "posted",
    lines: [
      dr("5000", aed(300_000), { taxCode: "SR", ctCategory: "deductible" }),
      dr("1450", aed(15_000), { taxCode: "SR" }),
      cr("2200", aed(315_000)),
    ],
  },
  {
    id: "je-1030", ref: "JE-1030", date: "2026-04-01",
    description: "Office rent & utilities — H1", entityId: "fz",
    txnCurrency: BASE, status: "posted",
    lines: [
      dr("6000", aed(120_000), { taxCode: "SR", ctCategory: "deductible" }),
      dr("1450", aed(6_000), { taxCode: "SR" }),
      cr("1100", aed(126_000)),
    ],
  },
  {
    id: "je-1035", ref: "JE-1035", date: "2026-05-14",
    description: "Paid suppliers", entityId: "fz",
    txnCurrency: BASE, status: "posted",
    lines: [dr("2200", aed(200_000)), cr("1100", aed(200_000))],
  },
  {
    id: "je-1040", ref: "JE-1040", date: "2026-06-16",
    description: "Intercompany recharge — shared services (to Distribution)",
    entityId: "fz", txnCurrency: BASE, status: "posted",
    lines: [
      dr("1500", aed(115_000)),
      cr("4900", aed(115_000), { ctCategory: "deductible" }),
    ],
  },
  {
    id: "je-1039", ref: "JE-1039", date: "2026-07-15",
    description: "FX revaluation — USD bank balance (gain)", entityId: "fz",
    txnCurrency: "USD", status: "posted",
    lines: [dr("1105", aed(8_000)), cr("7800", aed(8_000), { taxCode: "OS" })],
  },
  {
    id: "je-1042", ref: "JE-1042", date: "2026-07-18",
    description: "Client invoice — Falcon Logistics (USD 12,000)", entityId: "fz",
    txnCurrency: "USD", status: "posted",
    lines: [
      dr("1200", aedFils(4_407_600)),
      cr("4000", aedFils(4_197_714), { taxCode: "SR", ctCategory: "deductible" }),
      cr("2100", aedFils(209_886), { taxCode: "SR" }),
    ],
  },

  // ---- Mainland entity ----
  {
    id: "je-2001", ref: "JE-2001", date: "2026-01-06",
    description: "Opening share capital", entityId: "ml",
    txnCurrency: BASE, status: "posted",
    lines: [dr("1100", aed(200_000)), cr("3000", aed(200_000))],
  },
  {
    id: "je-2010", ref: "JE-2010", date: "2026-02-19",
    description: "Sales — domestic distribution (standard-rated)", entityId: "ml",
    txnCurrency: BASE, status: "posted",
    lines: [
      dr("1200", aed(315_000)),
      cr("4000", aed(300_000), { taxCode: "SR", ctCategory: "deductible" }),
      cr("2100", aed(15_000), { taxCode: "SR" }),
    ],
  },
  {
    id: "je-2015", ref: "JE-2015", date: "2026-03-11",
    description: "Cash collected from customers", entityId: "ml",
    txnCurrency: BASE, status: "posted",
    lines: [dr("1100", aed(250_000)), cr("1200", aed(250_000))],
  },
  {
    id: "je-2020", ref: "JE-2020", date: "2026-04-02",
    description: "Cost of sales — goods purchased", entityId: "ml",
    txnCurrency: BASE, status: "posted",
    lines: [
      dr("5000", aed(180_000), { taxCode: "SR", ctCategory: "deductible" }),
      dr("1450", aed(9_000), { taxCode: "SR" }),
      cr("2200", aed(189_000)),
    ],
  },
  {
    id: "je-2028", ref: "JE-2028", date: "2026-05-09",
    description: "Operating expenses — payroll & admin", entityId: "ml",
    txnCurrency: BASE, status: "posted",
    lines: [
      dr("6000", aed(90_000), { taxCode: "SR", ctCategory: "deductible" }),
      dr("1450", aed(4_500), { taxCode: "SR" }),
      cr("1100", aed(94_500)),
    ],
  },
  {
    id: "je-1041", ref: "JE-1041", date: "2026-07-17",
    description: "Supplier bill — DEWA utilities", entityId: "ml",
    txnCurrency: BASE, status: "posted",
    lines: [
      dr("6000", aed(3_150), { taxCode: "SR", ctCategory: "deductible" }),
      cr("2200", aed(3_150)),
    ],
  },
  {
    id: "je-2035", ref: "JE-2035", date: "2026-06-16",
    description: "Intercompany recharge — shared services (from Trading)",
    entityId: "ml", txnCurrency: BASE, status: "posted",
    lines: [
      dr("6500", aed(115_000), { ctCategory: "non-deductible" }),
      cr("2500", aed(115_000)),
    ],
  },
  {
    id: "je-2036", ref: "JE-2036", date: "2026-07-10",
    description: "Intercompany logistics recharge — pending FZ confirmation",
    entityId: "ml", txnCurrency: BASE, status: "posted",
    lines: [dr("6000", aed(12_000), { ctCategory: "deductible" }), cr("2500", aed(12_000))],
  },
  {
    id: "je-1038", ref: "JE-1038", date: "2026-07-15",
    description: "Payroll accrual — July (draft)", entityId: "ml",
    txnCurrency: BASE, status: "draft",
    lines: [dr("6000", aed(28_400)), cr("2200", aed(28_400))],
  },
];

/**
 * Build the full seed: base entries plus a per-entity corporate-tax accrual
 * derived from each entity's seeded profit before tax, using the core engine.
 */
export function buildSeedEntries(): JournalEntry[] {
  const entries = [...BASE_ENTRIES];

  for (const entity of SEED_ENTITIES) {
    const pnl = incomeStatement(BASE_ENTRIES, SEED_ACCOUNTS, BASE, entity.id);
    const est = estimateCt({
      profitBeforeTax: pnl.profitBeforeTax,
      revenue: pnl.revenue,
      rate: SEED_SETTINGS.ctRate,
      threshold: SEED_SETTINGS.ctThreshold,
      sbReliefRevenueCeiling: SEED_SETTINGS.sbReliefRevenueCeiling,
    });
    if (est.provision.amount > 0) {
      entries.push({
        id: `ct-${entity.id}`,
        ref: `CT-${entity.id.toUpperCase()}`,
        date: "2026-07-20",
        description: "Corporate tax provision (9%, estimated)",
        entityId: entity.id,
        txnCurrency: BASE,
        status: "posted",
        lines: [dr("8000", est.provision), cr("2600", est.provision)],
      });
    }
  }

  return entries;
}
